<script setup lang="ts">
import { useFetchRecruitisApi2 } from '@/js/utils/useFetchApi';
import Badge, { type ITheme as BadgeTheme } from '@ui/components/badge.vue';
import LoadingSpinner from '@ui/components/loadingSpinner.vue';
import { computed, ref, watch } from 'vue';

type MenuBadgeCount = number | string;

export interface TopBarMenuBadgeInterface {
    count?: MenuBadgeCount | null;
    countUrl?: string | null;
    theme?: BadgeTheme;
}

const props = withDefaults(defineProps<{
    badge?: TopBarMenuBadgeInterface | null;
    count?: MenuBadgeCount | null;
    shouldLoad?: boolean;
    variant?: 'row' | 'tab' | 'top-tab' | 'tab-icon';
}>(), {
    badge: null,
    count: undefined,
    shouldLoad: true,
    variant: 'row',
});

const count = ref<MenuBadgeCount | null>(null);
const loading = ref(false);

const isCount = (value: unknown): value is MenuBadgeCount => {
    return typeof value === 'number' || typeof value === 'string';
};

const directCount = computed<MenuBadgeCount | null>(() => {
    if (isCount(props.badge?.count)) {
        return props.badge.count;
    }

    if (isCount(props.count)) {
        return props.count;
    }

    return null;
});

const countUrl = computed(() => props.badge?.countUrl ?? null);
const hasCountUrl = computed(() => typeof countUrl.value === 'string' && countUrl.value.length > 0);
const isVisible = computed(() => loading.value || count.value !== null);
const badgeTheme = computed<BadgeTheme>(() => props.badge?.theme ?? 'dark-red');
const variantClass = computed(() => {
    switch (props.variant) {
        case 'tab':
            return 'font-brand ml-1 min-h-5 min-w-5 px-1 text-[11px] font-bold';
        case 'top-tab':
            return 'font-brand absolute right-3 -top-2.5 min-h-5 min-w-5 -translate-y-1 translate-x-1 px-1 text-[11px] font-bold';
        case 'tab-icon':
            return 'font-brand absolute -right-1 -top-1 min-h-4 min-w-4 px-1 text-[10px] font-bold';
        default:
            return 'font-brand -my-2 ml-auto h-6 min-w-[24px] px-1 text-xs font-bold text-white';
    }
});

const extractCount = (response: unknown): MenuBadgeCount | null => {
    if (isCount(response)) {
        return response;
    }

    if (response === null || typeof response !== 'object') {
        return null;
    }

    const payload = (response as { payload?: unknown }).payload;

    if (isCount(payload)) {
        return payload;
    }

    if (payload === null || typeof payload !== 'object') {
        return null;
    }

    const payloadCount = (payload as { count?: unknown }).count;
    return isCount(payloadCount) ? payloadCount : null;
};

const fetchCount = async () => {
    if (!props.shouldLoad || !hasCountUrl.value) {
        return;
    }

    try {
        loading.value = true;
        const { data } = await useFetchRecruitisApi2(countUrl.value ?? '').get().json();
        count.value = extractCount(data.value);
    } catch (error) {
        console.warn(error);
    } finally {
        loading.value = false;
    }
};

watch(
    directCount,
    (value) => {
        if (value !== null) {
            count.value = value;
        }
    },
    { immediate: true },
);

watch(
    [() => props.shouldLoad, countUrl],
    ([shouldLoad]) => {
        if (shouldLoad) {
            fetchCount();
        }
    },
    { immediate: true },
);
</script>

<template>
    <Badge
        v-if="isVisible"
        size="xsmall"
        :theme="badgeTheme"
        :outlined="false"
        circular
        class="relative shrink-0 overflow-hidden transition-all"
        :class="variantClass"
    >
        <div
            class="absolute inset-0 flex items-center justify-center"
            :class="loading ? 'opacity-100' : 'pointer-events-none opacity-0'"
        >
            <LoadingSpinner
                size="small"
                class="scale-75"
            ></LoadingSpinner>
        </div>
        <div
            class="grid overflow-hidden transition-all duration-150"
            :class="loading ? 'grid-cols-[0fr]' : 'grid-cols-[1fr]'"
        >
            <div
                class="overflow-hidden font-bold transition-all delay-75 duration-150"
                :class="loading ? '-translate-y-8 opacity-0' : 'translate-y-0 opacity-100'"
            >
                {{ count }}
            </div>
        </div>
    </Badge>
</template>
